import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const TIPO_VISITA_LABELS: Record<string, string> = {
    prospeccao: 'Prospecção',
    qualificacao: 'Qualificação',
    simulacao: 'Simulação',
    apresentacao: 'Apresentação',
    negociacao: 'Negociação',
    retorno: 'Retorno',
    pos_venda: 'Pós-venda',
};

export interface DiaResumo {
    total: number;
    visitas: number;
    retornos: number;
    kwh: number;
    presenciais: number; // visitas presenciais (entram na rota)
    ligacoes: number;    // visitas remotas ("Remotas do dia")
}

interface CalendarioMensalProps {
    mes: Date; // primeiro dia do mês exibido
    onChangeMes: (d: Date) => void;
    dataSel: string; // YYYY-MM-DD
    onSelectDate: (d: string) => void;
    resumoPorDia: Record<string, DiaResumo>;
    kmPorDia?: Record<string, number>; // km estimado das rotas por dia (visão macro do gestor)
}

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtKwhCompact = (v: number) => {
    if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1).replace('.', ',')}k kWh`;
    return `${Math.round(v)} kWh`;
};

/** Calendário mensal de visitas (Rotas v2): navegação, contadores e kWh por dia. */
const CalendarioMensal: React.FC<CalendarioMensalProps> = ({ mes, onChangeMes, dataSel, onSelectDate, resumoPorDia, kmPorDia }) => {
    const hoje = isoDate(new Date());

    const semanas = useMemo(() => {
        const primeiro = new Date(mes.getFullYear(), mes.getMonth(), 1);
        const inicio = new Date(primeiro);
        inicio.setDate(inicio.getDate() - inicio.getDay()); // volta até domingo
        const dias: Date[] = [];
        const cursor = new Date(inicio);
        for (let i = 0; i < 42; i++) {
            dias.push(new Date(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }
        // remove a última semana se for toda do mês seguinte
        const rows: Date[][] = [];
        for (let i = 0; i < 6; i++) {
            const semana = dias.slice(i * 7, i * 7 + 7);
            if (i > 0 && semana.every(d => d.getMonth() !== mes.getMonth())) break;
            rows.push(semana);
        }
        return rows;
    }, [mes]);

    const anos = useMemo(() => {
        const atual = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, i) => atual - 2 + i);
    }, []);

    const mudarMes = (delta: number) => {
        onChangeMes(new Date(mes.getFullYear(), mes.getMonth() + delta, 1));
    };

    const irHoje = () => {
        const agora = new Date();
        onChangeMes(new Date(agora.getFullYear(), agora.getMonth(), 1));
        onSelectDate(isoDate(agora));
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
            {/* Navegação */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-1">
                    <button onClick={() => mudarMes(-1)} className="p-1.5 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Mês anterior">
                        <ChevronLeft size={18} />
                    </button>
                    <button onClick={() => mudarMes(1)} className="p-1.5 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Próximo mês">
                        <ChevronRight size={18} />
                    </button>
                    <button onClick={irHoje} className="ml-1 px-3 py-1.5 text-xs font-bold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                        Hoje
                    </button>
                </div>
                <div className="flex items-center gap-1.5">
                    <select
                        value={mes.getMonth()}
                        onChange={e => onChangeMes(new Date(mes.getFullYear(), Number(e.target.value), 1))}
                        className="text-sm font-bold px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none"
                    >
                        {MESES.map((m, i) => <option key={m} value={i}>{m}</option>)}
                    </select>
                    <select
                        value={mes.getFullYear()}
                        onChange={e => onChangeMes(new Date(Number(e.target.value), mes.getMonth(), 1))}
                        className="text-sm font-bold px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none"
                    >
                        {anos.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
            </div>

            {/* Cabeçalho dos dias */}
            <div className="grid grid-cols-7 mb-1">
                {DIAS_SEMANA.map(d => (
                    <p key={d} className="text-center text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest py-1">{d}</p>
                ))}
            </div>

            {/* Grade */}
            <div className="grid grid-cols-7 gap-1">
                {semanas.flat().map(d => {
                    const iso = isoDate(d);
                    const doMes = d.getMonth() === mes.getMonth();
                    const resumo = resumoPorDia[iso];
                    const km = kmPorDia?.[iso];
                    const selecionado = iso === dataSel;
                    const ehHoje = iso === hoje;
                    return (
                        <button
                            key={iso}
                            onClick={() => onSelectDate(iso)}
                            className={`relative min-h-[64px] sm:min-h-[74px] rounded-xl p-1.5 text-left border transition-all flex flex-col
                                ${selecionado
                                    ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                    : 'border-transparent hover:border-gray-200 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40'}
                                ${doMes ? '' : 'opacity-35'}`}
                        >
                            <span className={`text-[11px] font-bold w-5 h-5 flex items-center justify-center rounded-full mb-0.5
                                ${ehHoje ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                                {d.getDate()}
                            </span>
                            {resumo && resumo.total > 0 && (
                                <div className="space-y-0.5 overflow-hidden">
                                    {resumo.presenciais > 0 && (
                                        <span className="block text-[9px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 rounded px-1 py-px truncate">
                                            📍 {resumo.presenciais} visita{resumo.presenciais > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {resumo.ligacoes > 0 && (
                                        <span className="block text-[9px] font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 rounded px-1 py-px truncate">
                                            📞 {resumo.ligacoes} remota{resumo.ligacoes > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {resumo.presenciais === 0 && resumo.ligacoes === 0 && (
                                        <span className="block text-[9px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded px-1 py-px truncate">
                                            {resumo.total} ativ.
                                        </span>
                                    )}
                                    {resumo.kwh > 0 && (
                                        <span className="hidden sm:block text-[9px] font-semibold text-amber-600 dark:text-amber-400 truncate">
                                            ⚡ {fmtKwhCompact(resumo.kwh)}
                                        </span>
                                    )}
                                    {km != null && km > 0 && (
                                        <span className="hidden sm:block text-[9px] font-semibold text-indigo-600 dark:text-indigo-400 truncate">
                                            🚗 ~{Math.round(km)} km
                                        </span>
                                    )}
                                </div>
                            )}
                            {(!resumo || resumo.total === 0) && km != null && km > 0 && (
                                <span className="hidden sm:block text-[9px] font-semibold text-indigo-600 dark:text-indigo-400 truncate">
                                    🚗 ~{Math.round(km)} km
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default CalendarioMensal;
